import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { Download, Loader2, BarChart3 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { PageTitle } from '@/components/page-title';
import { useNotification } from '@/components/notifications';
import { MembersTableContainer } from '@/components/members/members-table-container';
import { getAllMembersByOrg } from '@/services/members/get-all-members-by-org';
import { getOrgInfo } from '@/services/org/get-org-info';
import { OrgBrandingTab } from '@/components/admin/org-branding-tab';
import { OrgSsoTab } from '@/components/admin/org-sso-tab';
import { OrgApiKeysTab } from '@/components/admin/org-api-keys-tab';
import { OrgNodesToolsTab } from '@/components/admin/org-nodes-tools-tab';
import { ExportMetricsDialog } from '@/components/admin/export-metrics-dialog';
import {
  startWorkflowExport,
  getWorkflowExportStatus,
} from '@/services/admin/export-workflows';
import * as Sentry from '@sentry/react';

export const OrgDetailsPage = () => {
  const { targetOrgId: orgId } = useParams<{ targetOrgId: string }>();
  const notification = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'members';

  const [organization, setOrganization] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const fetchMembers = useCallback(async () => {
    if (!orgId) return { detail: 'Org ID needed' };
    return await getAllMembersByOrg(orgId);
  }, [orgId]);

  useEffect(() => {
    const loadOrgData = async () => {
      if (!orgId) {
        notification.error('Organization ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getOrgInfo(orgId);

        if (response?.detail) {
          notification.error(response.detail);
          return;
        }

        if (response?.data) {
          const orgData = response.data as unknown as {
            orgid: string;
            name: string;
          };
          setOrganization({
            id: orgData.orgid,
            name: orgData.name,
          });
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load organization data';
        notification.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadOrgData();
  }, [orgId, notification]);

  const [exporting, setExporting] = useState(false);
  const [exportMetricsOpen, setExportMetricsOpen] = useState(false);
  const exportPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollCountRef = useRef(0);

  const MAX_POLL_ATTEMPTS = 60;

  useEffect(() => {
    return () => {
      if (exportPollRef.current) clearTimeout(exportPollRef.current);
    };
  }, [orgId]);

  useEffect(() => {
    setExporting(false);
    if (exportPollRef.current) clearTimeout(exportPollRef.current);
    pollCountRef.current = 0;
  }, [orgId]);

  const handleExportAllWorkflows = useCallback(async () => {
    if (!orgId || exporting) return;

    setExporting(true);
    pollCountRef.current = 0;

    try {
      const { job_id } = await startWorkflowExport(orgId);
      notification.info(
        'Export started — you will be notified when the download is ready.',
      );

      const pollStatus = async () => {
        pollCountRef.current += 1;

        if (pollCountRef.current > MAX_POLL_ATTEMPTS) {
          if (exportPollRef.current) clearTimeout(exportPollRef.current);
          setExporting(false);
          notification.error(
            'Export timed out after 3 minutes. Please try again.',
          );
          return;
        }

        try {
          const status = await getWorkflowExportStatus(orgId, job_id);

          if (status.status === 'completed') {
            if (exportPollRef.current) clearTimeout(exportPollRef.current);
            setExporting(false);

            if (status.download_url) {
              notification.success(
                `Export ready! ${status.workflow_count ?? 0} workflow(s) exported.`,
              );
              window.location.href = status.download_url;
            } else {
              notification.warning(
                status.message || 'No workflows found to export.',
              );
            }
          } else if (status.status === 'failed') {
            if (exportPollRef.current) clearTimeout(exportPollRef.current);
            setExporting(false);
            notification.error(
              status.error || 'Export failed. Please try again.',
            );
          } else {
            exportPollRef.current = setTimeout(pollStatus, 3000);
          }
        } catch (pollError) {
          if (exportPollRef.current) clearTimeout(exportPollRef.current);
          setExporting(false);
          Sentry.captureException(pollError, {
            extra: { context: 'export-poll' },
          });
          notification.error('Failed to check export status.');
        }
      };

      exportPollRef.current = setTimeout(pollStatus, 3000);
    } catch (error) {
      setExporting(false);
      Sentry.captureException(error, { extra: { context: 'export-start' } });
      notification.error(
        error instanceof Error ? error.message : 'Failed to start export.',
      );
    }
  }, [orgId, exporting, notification]);

  if (loading) {
    return (
      <div className='w-full'>
        <div className='flex items-center justify-center py-12'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          <span className='ml-2 text-muted-foreground'>Loading...</span>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className='w-full border rounded-md p-6 bg-background'>
        <div className='flex items-center justify-center py-12'>
          <div className='text-center'>
            <p className='text-destructive font-medium'>Error</p>
            <p className='text-sm text-muted-foreground mt-1'>
              Failed to load organization
            </p>
          </div>
        </div>
      </div>
    );
  }

  const orgName = organization?.name || orgId || 'Unknown Organization';

  return (
    <div className='w-full'>
      <div className='mb-4 flex items-center justify-between'>
        <PageTitle title={orgName} />
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setExportMetricsOpen(true)}
          >
            <BarChart3 className='size-4 mr-2' />
            Export Metrics
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={handleExportAllWorkflows}
            disabled={exporting}
          >
            {exporting ? (
              <Loader2 className='size-4 mr-2 animate-spin' />
            ) : (
              <Download className='size-4 mr-2' />
            )}
            {exporting ? 'Exporting...' : 'Export All Workflows'}
          </Button>
        </div>
      </div>

      {orgId && (
        <ExportMetricsDialog
          orgId={orgId}
          open={exportMetricsOpen}
          onOpenChange={setExportMetricsOpen}
        />
      )}

      <div className='space-y-6'>
        <p className='text-sm text-muted-foreground'>Manage {orgName}</p>

        <Tabs
          defaultValue={defaultTab}
          onValueChange={handleTabChange}
          className='w-full'
        >
          <TabsList className='grid w-full grid-cols-5 mb-6'>
            <TabsTrigger value='members'>Members</TabsTrigger>
            <TabsTrigger value='branding'>Branding</TabsTrigger>
            <TabsTrigger value='sso'>Authentication</TabsTrigger>
            <TabsTrigger value='api-keys'>API Keys</TabsTrigger>
            <TabsTrigger value='nodes'>Nodes & Tools</TabsTrigger>
          </TabsList>

          <TabsContent value='members'>
            <div className='mt-4'>
              <MembersTableContainer
                organizationId={orgId}
                title=''
                fetchMembers={fetchMembers}
              />
            </div>
          </TabsContent>

          <TabsContent value='branding'>
            {orgId && <OrgBrandingTab orgId={orgId} />}
          </TabsContent>

          <TabsContent value='sso'>
            {orgId && <OrgSsoTab orgId={orgId} />}
          </TabsContent>

          <TabsContent value='api-keys'>
            {orgId && <OrgApiKeysTab orgId={orgId} />}
          </TabsContent>

          <TabsContent value='nodes'>
            {orgId && <OrgNodesToolsTab orgId={orgId} />}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
